import type {
  AttachmentBlockModel,
  AttachmentPdfSignaturePlacement,
} from '@blocksuite/affine/model';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';

const MIN_SIGNATURE_RECT_SIZE = 0.04;
export const PDF_SIGNATURE_DEFAULT_RECT = {
  width: 0.34,
  height: 0.08,
} as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function normalizePdfSignatureRect(rect: {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const width = clamp(rect.width, MIN_SIGNATURE_RECT_SIZE, 1);
  const height = clamp(rect.height, MIN_SIGNATURE_RECT_SIZE, 1);
  const x = clamp(rect.x, 0, 1 - width);
  const y = clamp(rect.y, 0, 1 - height);
  return { x, y, width, height };
}

export function getStoredPdfSignaturePlacements(
  model?: AttachmentBlockModel
): AttachmentPdfSignaturePlacement[] {
  if (!model) return [];
  const props = model.props as unknown as {
    pdfSignaturePlacements?: AttachmentPdfSignaturePlacement[];
  };
  return props.pdfSignaturePlacements ?? [];
}

export function useStoredPdfSignaturePlacements(
  model?: AttachmentBlockModel
): AttachmentPdfSignaturePlacement[] {
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!model) return;
    const subscription = model.store.slots.blockUpdated.subscribe(payload => {
      if (payload.type !== 'update') return;
      if (payload.id !== model.id) return;
      const key = (payload.props as { key?: string } | undefined)?.key;
      if (key && key !== 'pdfSignaturePlacements') return;
      setVersion(current => current + 1);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [model]);

  return getStoredPdfSignaturePlacements(model);
}

function createDefaultSignatureRect(center?: { x: number; y: number }) {
  const rect = center
    ? {
        x: center.x - PDF_SIGNATURE_DEFAULT_RECT.width / 2,
        y: center.y - PDF_SIGNATURE_DEFAULT_RECT.height / 2,
        width: PDF_SIGNATURE_DEFAULT_RECT.width,
        height: PDF_SIGNATURE_DEFAULT_RECT.height,
      }
    : {
        x: 0.5 - PDF_SIGNATURE_DEFAULT_RECT.width / 2,
        y: 0.72,
        width: PDF_SIGNATURE_DEFAULT_RECT.width,
        height: PDF_SIGNATURE_DEFAULT_RECT.height,
      };
  return normalizePdfSignatureRect(rect);
}

export function upsertPdfSignaturePlacement({
  model,
  appearanceId,
  pageIndex,
  center,
}: {
  model?: AttachmentBlockModel;
  appearanceId: string;
  pageIndex: number;
  center?: { x: number; y: number };
}): AttachmentPdfSignaturePlacement | null {
  if (!model) return null;
  const sourceId = model.props.sourceId;
  if (!sourceId) return null;
  const current = getStoredPdfSignaturePlacements(model);
  const placement: AttachmentPdfSignaturePlacement = {
    id: nanoid(),
    signatureAppearanceId: appearanceId,
    sourceId,
    pageIndex,
    rect: createDefaultSignatureRect(center),
    rotation: 0,
    createdAt: Date.now(),
  };
  const next = [
    ...current.filter(
      item =>
        !(
          item.sourceId === sourceId &&
          item.signatureAppearanceId === appearanceId
        )
    ),
    placement,
  ];
  model.store.updateBlock(model, {
    pdfSignaturePlacements: next,
  } as Partial<AttachmentBlockModel['props']>);
  return placement;
}

export function createPdfSignaturePlacement({
  model,
  appearanceId,
  pageIndex,
  center,
}: {
  model?: AttachmentBlockModel;
  appearanceId: string;
  pageIndex: number;
  center?: { x: number; y: number };
}): AttachmentPdfSignaturePlacement | null {
  if (!model) return null;
  const sourceId = model.props.sourceId;
  if (!sourceId) return null;
  const placement: AttachmentPdfSignaturePlacement = {
    id: nanoid(),
    signatureAppearanceId: appearanceId,
    sourceId,
    pageIndex,
    rect: createDefaultSignatureRect(center),
    rotation: 0,
    createdAt: Date.now(),
  };
  model.store.updateBlock(model, {
    pdfSignaturePlacements: [
      ...getStoredPdfSignaturePlacements(model),
      placement,
    ],
  } as Partial<AttachmentBlockModel['props']>);
  return placement;
}

export function pastePdfSignaturePlacement({
  model,
  placement: copiedPlacement,
  pageIndex = copiedPlacement.pageIndex,
}: {
  model?: AttachmentBlockModel;
  placement: Pick<
    AttachmentPdfSignaturePlacement,
    'signatureAppearanceId' | 'pageIndex' | 'rect' | 'rotation'
  >;
  pageIndex?: number;
}): AttachmentPdfSignaturePlacement | null {
  if (!model) return null;
  const sourceId = model.props.sourceId;
  if (!sourceId || !copiedPlacement.signatureAppearanceId) return null;
  const placement: AttachmentPdfSignaturePlacement = {
    id: nanoid(),
    signatureAppearanceId: copiedPlacement.signatureAppearanceId,
    sourceId,
    pageIndex,
    rect: normalizePdfSignatureRect({
      ...copiedPlacement.rect,
      x: copiedPlacement.rect.x + 0.02,
      y: copiedPlacement.rect.y + 0.02,
    }),
    rotation: copiedPlacement.rotation,
    createdAt: Date.now(),
  };
  model.store.updateBlock(model, {
    pdfSignaturePlacements: [
      ...getStoredPdfSignaturePlacements(model),
      placement,
    ],
  } as Partial<AttachmentBlockModel['props']>);
  return placement;
}

export function updatePdfSignaturePlacement({
  model,
  placementId,
  patch,
}: {
  model?: AttachmentBlockModel;
  placementId: string;
  patch: Partial<
    Pick<AttachmentPdfSignaturePlacement, 'pageIndex' | 'rect' | 'rotation'>
  >;
}): AttachmentPdfSignaturePlacement | null {
  if (!model) return null;
  const current = getStoredPdfSignaturePlacements(model);
  let updated: AttachmentPdfSignaturePlacement | null = null;
  const next = current.map(placement => {
    if (placement.id !== placementId) return placement;
    updated = {
      ...placement,
      ...patch,
      rect: patch.rect ? normalizePdfSignatureRect(patch.rect) : placement.rect,
      rotation:
        typeof patch.rotation === 'number' && Number.isFinite(patch.rotation)
          ? patch.rotation
          : placement.rotation,
    };
    return updated;
  });
  if (!updated) return null;
  model.store.updateBlock(model, {
    pdfSignaturePlacements: next,
  } as Partial<AttachmentBlockModel['props']>);
  return updated;
}

export function removePdfSignaturePlacement({
  model,
  placementId,
}: {
  model?: AttachmentBlockModel;
  placementId: string;
}): void {
  if (!model) return;
  const current = getStoredPdfSignaturePlacements(model);
  const next = current.filter(placement => placement.id !== placementId);
  if (next.length === current.length) return;
  model.store.updateBlock(model, {
    pdfSignaturePlacements: next,
  } as Partial<AttachmentBlockModel['props']>);
}
